import axios, {
  type AxiosError,
  type AxiosInstance,
  type InternalAxiosRequestConfig,
} from 'axios';
import {
  getIdToken,
  isFirebaseConfigured,
  waitForFirebaseAuthReady,
  FIREBASE_AUTH_READY_TIMEOUT_MS,
} from '@/lib/firebase';
import { useAuthStore } from '@/stores/auth-store';
import { useWarehouseStore } from '@/stores/warehouse-store';
import { useChannelStore } from '@/stores/channel-store';

// API URL - uses Vite proxy in dev, Cloud Run URL in production
const API_URL = import.meta.env.VITE_API_URL || '';

// Anchored to actual page-load timestamp (stable across code-splitting and
// lazy imports). Used by the response interceptor to identify 401s that arrive
// during the Firebase-rehydration window, so they don't trigger a spurious
// logout.
const PAGE_LOAD_TIME =
  typeof performance !== 'undefined' && performance.timeOrigin
    ? performance.timeOrigin
    : Date.now();
// Derived from the gate timeout so the two stay linked. If the gate times out
// (Firebase init slow), requests fire un-authed and the 401 arrives one API
// round-trip *after* the gate's deadline — the +2000ms buffer covers a Cloud
// Run cold-start round-trip so that 401 still lands inside the grace window
// and doesn't flip the user to logged-out before the gate had its chance.
const FIREBASE_INIT_GRACE_MS = FIREBASE_AUTH_READY_TIMEOUT_MS + 2000;

/**
 * Resolve a document storage URL to an absolute URL.
 * In production the API is on a different domain, so relative paths like
 * `/api/documents/file/...` need the API base URL prepended.
 */
export function resolveDocumentUrl(
  storageUrl: string | null | undefined,
): string {
  if (!storageUrl) return '';
  // Already absolute
  if (storageUrl.startsWith('http://') || storageUrl.startsWith('https://')) {
    return storageUrl;
  }
  // Relative path — prepend API base URL
  return `${API_URL}${storageUrl}`;
}

// Test user ID for development - this bypasses Firebase auth
const TEST_USER_ID =
  import.meta.env.VITE_TEST_USER_ID || 'd3913fb6-82c9-46d7-bdcf-fa1bd0c0dd44';

/**
 * Companion secret for the `x-test-user-id` bypass. A local API leaves
 * `TEST_USER_SECRET` unset and ignores this; a DEPLOYED dev API requires it,
 * so pointing this dev server at wms-api-dev needs `VITE_TEST_USER_SECRET`
 * (value in the gitignored `.claude/local/dev-test-user-secret.txt`).
 * Read behind `import.meta.env.DEV` so a production build folds this to `''`
 * at compile time: `testUserHeaders` is exported, so without the guard a
 * `VITE_TEST_USER_SECRET` set during a prod build would be inlined into the
 * public bundle even though the calling branch is dead there.
 */
const TEST_USER_SECRET = import.meta.env.DEV
  ? import.meta.env.VITE_TEST_USER_SECRET || ''
  : '';

/** Test-user dev-bypass headers; includes the secret when one is configured. */
export function testUserHeaders(): Record<string, string> {
  return TEST_USER_SECRET
    ? { 'x-test-user-id': TEST_USER_ID, 'x-test-user-secret': TEST_USER_SECRET }
    : { 'x-test-user-id': TEST_USER_ID };
}

/**
 * Socket.IO handshake twin of {@link testUserHeaders} — the gateways gate the
 * `userId` dev passthrough on the same secret, so a socket must send it too.
 * Takes the caller's own test-user id (the socket hooks each have their own).
 */
export function testUserSocketAuth(userId: string): Record<string, string> {
  return TEST_USER_SECRET
    ? { userId, testUserSecret: TEST_USER_SECRET }
    : { userId };
}

export const apiClient: AxiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

// Request interceptor for auth tokens
apiClient.interceptors.request.use(
  async (config) => {
    // Add warehouse context header for all requests. "All Warehouses" mode
    // only applies to admins — for non-admins we always send the header even
    // if isAllMode was persisted (e.g. stale state after an admin demotion).
    const { selectedWarehouseId, isAllMode } = useWarehouseStore.getState();
    const isAdmin = useAuthStore.getState().user?.isAdmin ?? false;
    const effectiveAllMode = isAllMode && isAdmin;
    if (selectedWarehouseId && !effectiveAllMode) {
      config.headers['x-warehouse-id'] = selectedWarehouseId;
    }

    // Add business-channel view filter header (view-only, never a security
    // boundary). Omitted for the unfiltered `ALL` default.
    const { selectedChannel } = useChannelStore.getState();
    if (selectedChannel !== 'ALL') {
      config.headers['x-channel'] = selectedChannel;
    }

    // In dev mode without Firebase, use test user header
    if (import.meta.env.DEV && !isFirebaseConfigured() && TEST_USER_ID) {
      Object.assign(config.headers, testUserHeaders());
      return config;
    }

    // Skip if Authorization header was already set by the caller
    if (!config.headers.Authorization) {
      // Prefer WMS-issued JWT (username login path) over Firebase token.
      const { wmsToken, wmsTokenExpiresAt } = useAuthStore.getState();
      const wmsValid =
        wmsToken &&
        (!wmsTokenExpiresAt ||
          new Date(wmsTokenExpiresAt).getTime() > Date.now());
      if (wmsValid && wmsToken) {
        config.headers.Authorization = `Bearer ${wmsToken}`;
      } else {
        if (isFirebaseConfigured() && useAuthStore.getState().isAuthenticated) {
          await waitForFirebaseAuthReady();
        }
        try {
          const token = await getIdToken();
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          }
        } catch {
          // Token fetch failed - request will likely get a 401
        }
      }
    }

    return config;
  },
  (error) => Promise.reject(error),
);

/**
 * A 403 carrying one of the JwtAuthGuard's session-death messages means the
 * WMS session itself is gone (revoked on another device, expired, or deleted)
 * — not a permissions failure. Treat it like a 401 so the user is logged out
 * and redirected instead of staring at a silently broken page. Authorization
 * 403s (e.g. Nest's default "Forbidden resource") are NOT matched and must
 * never trigger a logout.
 */
export function isSessionDeath403(error: AxiosError): boolean {
  if (error.response?.status !== 403) return false;
  const msg =
    (error.response?.data as { message?: string } | undefined)?.message ?? '';
  return (
    msg.startsWith('Session ') || // "Session not found" | "Session revoked (…)" | "Session expired"
    msg === 'User account is deactivated'
  );
}

/**
 * Response-error handler (exported for unit testing). On a Firebase-authed 401
 * with an expired ID token, performs EXACTLY ONE force-refresh + retry before
 * giving up. A WMS-token 401 and a session-death 403 are never refresh-retried
 * — they log out immediately.
 */
export async function onResponseError(error: AxiosError) {
  const isUnauthorized = error.response?.status === 401;
  if (isUnauthorized || isSessionDeath403(error)) {
    const sinceLoad = Date.now() - PAGE_LOAD_TIME;
    if (
      isUnauthorized &&
      isFirebaseConfigured() &&
      sinceLoad < FIREBASE_INIT_GRACE_MS
    ) {
      // Likely a request that slipped past the readiness gate during Firebase
      // rehydration. Don't log the user out — let the next refetch retry with
      // a real token. (A session-death 403 is never a Firebase-init race.)
      return Promise.reject(error);
    }

    // One-shot Firebase token refresh + retry. Firebase ID tokens expire ~1h;
    // a stale-token 401 is recoverable with a single getIdToken(true). Gate it
    // to genuine Firebase-authed 401s: a WMS-token 401 (username login path)
    // won't be fixed by a Firebase refresh, so it must fall through to logout.
    // `_retried` caps this at one attempt so a persistent 401 can't loop.
    const config = error.config as
      | (InternalAxiosRequestConfig & { _retried?: boolean })
      | undefined;
    const { wmsToken, wmsTokenExpiresAt } = useAuthStore.getState();
    const wmsValid =
      !!wmsToken &&
      (!wmsTokenExpiresAt ||
        new Date(wmsTokenExpiresAt).getTime() > Date.now());
    if (
      isUnauthorized &&
      config &&
      !config._retried &&
      isFirebaseConfigured() &&
      !wmsValid
    ) {
      try {
        const freshToken = await getIdToken(true);
        if (freshToken) {
          config._retried = true;
          config.headers = config.headers ?? {};
          config.headers.Authorization = `Bearer ${freshToken}`;
          return apiClient(config);
        }
      } catch {
        // Force-refresh failed — fall through to logout below.
      }
    }

    if (
      (import.meta.env.PROD || isFirebaseConfigured()) &&
      window.location.pathname !== '/login'
    ) {
      // Use store logout instead of hard reload to avoid infinite reload loops
      useAuthStore.getState().logout();
    } else if (!isFirebaseConfigured()) {
      console.warn(
        `${isUnauthorized ? '401 Unauthorized' : '403 session expired/revoked'} - API authentication failed. Check if the API is running and configured correctly.`,
      );
    }
  }
  return Promise.reject(error);
}

// Response interceptor for error handling
apiClient.interceptors.response.use((response) => response, onResponseError);

/**
 * POST a FormData payload via apiClient.
 * Passing `Content-Type: undefined` lets the browser set the correct
 * multipart/form-data boundary automatically, overriding the default
 * application/json header configured on the axios instance.
 * `timeout` overrides the instance's 30 s for uploads the server parses
 * at length (e.g. the kitchen catalog workbook).
 */
export function postMultipart<T>(
  url: string,
  formData: FormData,
  options?: { timeout?: number },
) {
  return apiClient.post<T>(url, formData, {
    headers: { 'Content-Type': undefined },
    ...(options?.timeout !== undefined && { timeout: options.timeout }),
  });
}

export interface ApiError {
  /** NestJS ValidationPipe sends a string[]; everything else sends a string. */
  message: string | string[];
  statusCode: number;
  error?: string;
  /**
   * Machine-readable cause on coded refusals (`gather_in_progress`,
   * `hold_window_in_past`, …). Route on this, never on the HTTP status
   * alone — one status covers several causes on most routes.
   */
  code?: string;
}

export function isApiError(error: unknown): error is AxiosError<ApiError> {
  return axios.isAxiosError(error);
}

export function getErrorMessage(error: unknown): string {
  if (isApiError(error)) {
    // NestJS ValidationPipe returns `message` as a string[] — joined here so
    // no call site returns a raw array where a string is expected.
    const m = error.response?.data?.message;
    if (Array.isArray(m)) return m.join('; ');
    return m || error.message || 'An error occurred';
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'An unexpected error occurred';
}

/**
 * {@link getErrorMessage} for a request made with `responseType: 'blob'`:
 * the error's JSON body arrives as a Blob, which `getErrorMessage` reads past.
 */
export async function messageFromBlobError(err: unknown): Promise<string> {
  if (isApiError(err) && err.response?.data instanceof Blob) {
    try {
      const parsed: unknown = JSON.parse(await err.response.data.text());
      const message = (parsed as { message?: string | string[] })?.message;
      if (Array.isArray(message)) return message.join('; ');
      if (message) return message;
    } catch {
      // Not JSON (a proxy error page, an empty body) — fall through to the
      // generic message rather than surfacing raw bytes to the manager.
    }
  }
  return getErrorMessage(err);
}
